import { attachAutomaticValidator, ManualEntityValidator, validate } from "@frui.ts/validation/dist";
import { Modules } from "config";
import { action, computed, observable } from "mobx";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type NotificationService from "services/notificationService";
import type SecurityService from "services/securityService";
import { BankAccountFormatType } from "entities/bankAccountFormatType";
import type OptionsService from "services/optionsService";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import CreateViewModel from "viewModels/createViewModel";
import type AlertsViewModel from "viewModels/alertsViewModel";
import InternalReportRequest, { InternalReportRequestValidationRules } from "entities/internalReportRequest";
import type InternalReportsRepository from "repositories/internalReportsRepository";
import type MonthOptionService from "services/monthOptionService";
import InternalReportType from "entities/internalReportType";
import { watchBusy } from "@frui.ts/screens";
import { NotificationType } from "services/notificationService";
import * as Sentry from "@sentry/react";
import type InternalReportResponse from "entities/internalReportResponse";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import download from "downloadjs";
import { allowToDownload } from "./rights";
import ExemptionReportsListViewModel from "./exemptionReports/exemptionReportsListViewModel";
import ExemptionSummaryReportsListViewModel from "./exemptionSummaryReports/exemptionSummaryReportsListViewModel";
import RevenueReportsListViewModel from "./revenueReports/revenueReportsListViewModel";
import ExpenseReportsListViewModel from "./expenseReports/expenseReportsListViewModel";
import { internalReportType } from "services/eventBus";

export default class InternalReportsCreateViewModel extends CreateViewModel<InternalReportRequest> {
  readonly modelName = "internalReport";
  private unsubscriptions: Array<() => void> = [];
  countrySelectViewModel: CountrySelectViewModel;
  private manualValidator = new ManualEntityValidator<InternalReportRequest>(true);
  @observable fileResponse: any = undefined;
  @observable vmParent: any = undefined;
  @observable addNew = false;

  @computed
  get accountFormatTypes() {
    return this.optionsService.internalEnumOptions(BankAccountFormatType);
  }

  constructor(
    protected repository: InternalReportsRepository,
    protected localizationService: LocalizationService,
    protected securityService: SecurityService,
    protected monthOptionService: MonthOptionService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    private optionsService: OptionsService,
    public alertsViewModel: AlertsViewModel
  ) {
    super();
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
  }

  @action.bound
  protected loadDetail() {
    return new InternalReportRequest();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }
  @action.bound
  onHide() {
    this.requestClose();
  }

  @action.bound
  @watchBusy
  async onFormSubmit() {
    if (!validate(this.item)) {
      return;
    }
    this.setExportName();
    await this.onGenerateReportClick();
  }

  @action.bound
  @watchBusy
  async onGenerateReportClick() {
    try {
      const result = await this.repository.generateNewReport(this.item, this.item.name);
      this.eventBus.publish(internalReportType({ internalReportType: this.item.report }));
      if (result.reportRequestId) {
        const fileName = `${this.item.name}_report_${new Date().toISOString()}.xlsx`;

        await this.download({ id: result.reportRequestId, fileName: fileName, createdBy: "", status: result.reportStatus });
        this.addNew = false;
        this.requestClose();
        this.notificationService.add({
          title: this.translate(`${this.item.name}.alreadyGeneratedMessage`),
          type: NotificationType.SUCCESS,
        });
      } else {
        this.addNew = false;
        this.requestClose();
        this.notificationService.add({
          title: this.translate(`${this.item.name}.generatingMessage`),
          type: NotificationType.SUCCESS,
        });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  async download(item: InternalReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.fileName), "application/zip");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  @computed
  get monthlyOptions() {
    return this.monthOptionService.monthOptions;
  }

  @computed
  get internalReportTypeOptions() {
    const formatOptions = (item: string) => ({
      id: item,
      label: this.translate(item),
    });
    return Object.values(InternalReportType)
      .filter(
        (item: string) =>
          item !== InternalReportType.RoadTollPaymentStatistics &&
          item !== InternalReportType.RoadsideChecks &&
          item !== InternalReportType.BusinessReport
      )
      .map(formatOptions);
  }

  @action.bound
  setDefaultData(vmParent: any) {
    this.item = new InternalReportRequest();
    attachAutomaticValidator(this.item, InternalReportRequestValidationRules);

    if (vmParent instanceof ExemptionReportsListViewModel) {
      this.item.report = InternalReportType.Exemptions;
      this.item.name = "exemptions";
    }
    if (vmParent instanceof ExemptionSummaryReportsListViewModel) {
      this.item.report = InternalReportType.ExemptionsSummary;
      this.item.name = "exemptions_summary";
    }
    if (vmParent instanceof ExpenseReportsListViewModel) {
      this.item.report = InternalReportType.Expenses;
      this.item.name = "expenses";
    }
    if (vmParent instanceof RevenueReportsListViewModel) {
      this.item.report = InternalReportType.Revenues;
      this.item.name = "revenues";
    }
  }

  @action.bound
  setExportName() {
    if (this.item.report === InternalReportType.Exemptions) {
      this.item.name = "exemptions";
    }
    if (this.item.report === InternalReportType.ExemptionsSummary) {
      this.item.name = "exemptions_summary";
    }
    if (this.item.report === InternalReportType.Expenses) {
      this.item.name = "expenses";
    }
    if (this.item.report === InternalReportType.Revenues) {
      this.item.name = "revenues";
    }
  }

  @computed
  get reportTypeOptions() {
    return this.monthOptionService.monthOptions;
  }

  @computed
  get monthlyOptionsTo() {
    if (!this.item?.from) {
      return this.monthlyOptions;
    }

    // We want to take only the values until the previously selected one.
    const result: any[] = [];
    this.monthlyOptions.some(x => (result.push(x), x.id === this.item.from));

    return result;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `internalReports.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateAttribute(code: string, size?: number): string {
    return this.localizationService.translateAttribute("internalReports", code, size);
  }
}
